import { useCallback, useEffect, useState } from "react";

import { FixedClickableTextPanel } from "@/Common/Components/ClickableText/FixedClickableTextPanel";
import { SimpleTextareaResizable } from "@/Common/Components/Inputs/SimpleTextareaResizable";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";

import style from "./ProgressNoteSection.module.scss";

interface IProgressNoteSectionProps {
    isLongTitle?: boolean;
    title: string;
    encounterId: string;
    itemSize: number;
    sectionName: string;
    context: string;
    noteContent: string;
    isEditMode: boolean;
    isReadOnly: boolean;
    clickableTextVisible: boolean;
    showNotePopup: (noteContext: string, e: React.MouseEvent) => void;
    closeClickableText: () => void;
    onAutosaveFieldBlur: () => void;
    showClickableVisibility: (clickableText: string, e?: React.MouseEvent) => void;
    toggleAutoSave: () => void;
    operation: LocalPatientOperations.MedicalNote;
}

function ProgressNoteSection(props: IProgressNoteSectionProps): JSX.Element {
    const [content, setContent] = useState<string>(props.noteContent);

    useEffect(() => {
        setContent(props.noteContent ?? "");
    }, [props.noteContent]);

    const onItemChanged = useCallback(value => {
        sessionStore.dispatch(props.operation, { value });
        props.toggleAutoSave();
    }, [props.toggleAutoSave]);

    return (
        <tr className={style.progressNoteSectionRow}>
            <td className="c1 bottom-pad">
                <strong className={props.isLongTitle ? "note-long-name" : ""}>{props.title}</strong>
            </td>
            <td className="c2 bottom-pad">
                <div className="tb-row tb">
                    {!props.isReadOnly && (
                        <div className="add-btn-column">
                            <a onClick={e => props.showNotePopup(props.context, e)} data-cy={`medicalNote-add-${props.sectionName}`}>
                                {props.itemSize <= 0 ?
                                    <span>[+]</span> :
                                    <span>[<span className="red-text"> <strong>{props.itemSize}</strong></span>]</span>
                                }
                            </a>
                        </div>
                    )}
                    {!props.isEditMode && (
                        <div className="note-container medical-notes" data-cy={`medicalNote-${props.sectionName}`}>
                            {!IsNullOrWhiteSpace(props.noteContent) &&
                                JSXHelper.BreakStringIntoParagraphs(props.noteContent, true)
                            }
                        </div>
                    )}
                    {props.isEditMode && (
                        <div>
                            <SimpleTextareaResizable
                                data-cy={`medicalNote-${props.sectionName}`}
                                id={`note_${props.sectionName}_${props.encounterId}`}
                                value={content}
                                onChange={e => {
                                    setContent(e.target.value);
                                    onItemChanged(e.target.value);
                                }}
                                onBlur={() => props.onAutosaveFieldBlur?.()}
                                className={`vresize ${style.textArea}`}
                                minHeight="54px"
                                maxHeight="99999px"
                            />

                            <FixedClickableTextPanel
                                dataCy={`medicalNote-${props.sectionName}`}
                                cssClass="text-helper-container medical-note-clickable-text"
                                context={props.context}
                                inputElementId={`note_${props.sectionName}_${props.encounterId}`}
                                onCloseEvent={() => props.closeClickableText()}
                                isLinkDisabled={false}
                                isClickableTextPanelOpen={props.clickableTextVisible}
                                onClickableTextPanelOpen={() => props.showClickableVisibility(props.sectionName)}
                                onClickableTextPanelClose={() => props.closeClickableText()}
                            />
                        </div>
                    )}
                </div>
            </td>
        </tr>
    );
}

export { ProgressNoteSection };
